import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";

const PermissionsContext = createContext(null);

/**
 * Phase 8a.2.1 — admin permission shell.
 *
 * Fetches `/admin/me/permissions` on mount (only when the current user is an
 * admin) and caches:
 *   { team_role: "manager", permissions: ["view_all_users", ...], is_owner: false }
 *
 * UI uses `has("view_all_users")` for button-level guards and
 * `teamRole === "owner"` for owner-only flows (role change, member delete).
 */
export function AdminPermissionsProvider({ children }) {
  const { user, isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchPerms = useCallback(async () => {
    if (!isAdmin) {
      setData(null);
      return null;
    }
    setLoading(true);
    setError(null);
    try {
      const resp = await api.get("/admin/me/permissions");
      setData(resp.data);
      return resp.data;
    } catch (e) {
      setError(e);
      setData(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) fetchPerms();
    else setData(null);
  }, [isAdmin, user?.email, fetchPerms]);

  const value = useMemo(() => {
    const permsSet = new Set(data?.permissions || []);
    return {
      teamRole: data?.team_role || null,
      permissions: data?.permissions || [],
      isOwner: !!data?.is_owner,
      loading,
      error,
      refetch: fetchPerms,
      has: (perm) => permsSet.has(perm),
    };
  }, [data, loading, error, fetchPerms]);

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
}

export function useAdminPermissions() {
  const ctx = useContext(PermissionsContext);
  if (!ctx) {
    // Tolerant fallback so consumer pages outside the admin shell don't blow up.
    return { teamRole: null, permissions: [], isOwner: false, loading: false, error: null, has: () => false, refetch: async () => null };
  }
  return ctx;
}

export default AdminPermissionsProvider;
